import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl, API_ENDPOINTS } from '../config/api';
import { useTranslation } from 'react-i18next';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  position: string;
  role: string;
}

interface CompanyStats {
  totalPoints: number;
  availablePoints: number;
  allocatedPoints: number;
}

interface AwardPointsProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AwardPoints: React.FC<AwardPointsProps> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [companyStats, setCompanyStats] = useState<CompanyStats>({
    totalPoints: 0,
    availablePoints: 0,
    allocatedPoints: 0
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [awardType, setAwardType] = useState<'individual' | 'department' | 'all'>('individual');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [points, setPoints] = useState<string>('');
  const [reason, setReason] = useState<string>('');

  // Selected users for bulk operations
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  useEffect(() => {
    // Extract unique departments
    const uniqueDepartments = Array.from(new Set(users.map(user => user.department).filter(Boolean)));
    setDepartments(uniqueDepartments);
  }, [users]);

  useEffect(() => {
    // Update selected users based on award type
    if (awardType === 'department' && selectedDepartment) {
      const departmentUsers = users.filter(user => user.department === selectedDepartment);
      setSelectedUsers(departmentUsers);
    } else if (awardType === 'all') {
      setSelectedUsers(users);
    } else if (awardType === 'individual' && selectedUser) {
      const individualUser = users.find(user => user.id === selectedUser);
      setSelectedUsers(individualUser ? [individualUser] : []);
    } else {
      setSelectedUsers([]);
    }
  }, [awardType, selectedUser, selectedDepartment, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(getApiUrl(API_ENDPOINTS.DISTRIBUTION), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
      setCompanyStats({
        totalPoints: response.data.summary.totalPoints || 0,
        availablePoints: response.data.summary.availablePoints || 0,
        allocatedPoints: response.data.summary.allocatedPoints || 0
      });
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!points || parseFloat(points) === 0) {
      setError('Please enter a valid points amount');
      return;
    }

    if (selectedUsers.length === 0) {
      setError('Please select at least one user');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason for awarding points');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const token = localStorage.getItem('token');
      const pointsValue = parseFloat(points);

      // Use bulk award endpoint for better performance
      const response = await axios.post(getApiUrl(API_ENDPOINTS.BULK_AWARD), {
        userIds: selectedUsers.map(user => user.id),
        points: pointsValue,
        reason: reason,
        awardType: awardType
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const userCount = response.data.usersAffected;
      const pointsText = pointsValue > 0 ? 'awarded' : 'deducted';
      const pointsAmount = Math.abs(pointsValue);
      
      setSuccess(
        `Successfully ${pointsText} ${pointsAmount} points to ${userCount} user${userCount > 1 ? 's' : ''}`
      );

      // Reset form
      setAwardType('individual');
      setSelectedUser('');
      setSelectedDepartment('');
      setPoints('');
      setReason('');
      setSelectedUsers([]);

      // Call success callback
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);

    } catch (error: any) {
      console.error('Error awarding points:', error);
      setError(error.response?.data?.error || t('pointsDistribution.failedToAwardPoints'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setAwardType('individual');
      setSelectedUser('');
      setSelectedDepartment('');
      setPoints('');
      setReason('');
      setSelectedUsers([]);
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  const getAwardTypeDescription = () => {
    switch (awardType) {
      case 'individual':
        return t('pointsDistribution.awardTypeDescription.individual');
      case 'department':
        return t('pointsDistribution.awardTypeDescription.department', { department: selectedDepartment || t('pointsDistribution.department') });
      case 'all':
        return t('pointsDistribution.awardTypeDescription.all');
      default:
        return '';
    }
  };

  const getSelectedUsersText = () => {
    if (selectedUsers.length === 0) return t('pointsDistribution.noUsersSelected');
    if (selectedUsers.length === 1) {
      const user = selectedUsers[0];
      return `${user.firstName} ${user.lastName} (${user.email})`;
    }
    return `${selectedUsers.length} ${t('common.users')} ${t('common.selected')}`;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon />
          {t('pointsDistribution.awardPointsTitle')}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            {/* Company Points Info */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                {t('pointsDistribution.companyPointsStatus')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('pointsDistribution.totalPoints')}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {companyStats.totalPoints.toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('pointsDistribution.availablePoints')}
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {companyStats.availablePoints.toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('pointsDistribution.allocatedPoints')}
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {companyStats.allocatedPoints.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
              {selectedUsers.length > 0 && points && parseFloat(points) > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('pointsDistribution.totalPointsNeeded', { points: (parseFloat(points) * selectedUsers.length).toLocaleString() })}
                    {parseFloat(points) * selectedUsers.length > companyStats.availablePoints && (
                      <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                        {' '}({t('pointsDistribution.insufficientPoints')})
                      </span>
                    )}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Award Type Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('pointsDistribution.awardType')}</InputLabel>
              <Select
                value={awardType}
                onChange={(e) => setAwardType(e.target.value as 'individual' | 'department' | 'all')}
                label={t('pointsDistribution.awardType')}
              >
                <MenuItem value="individual">{t('pointsDistribution.individual')} {t('common.user')}</MenuItem>
                <MenuItem value="department">{t('pointsDistribution.department')}</MenuItem>
                <MenuItem value="all">{t('pointsDistribution.allUsers')}</MenuItem>
              </Select>
            </FormControl>

            {/* Type Description */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {getAwardTypeDescription()}
            </Typography>

            {/* User/Department Selection */}
            {awardType === 'individual' && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>{t('pointsDistribution.selectUser')}</InputLabel>
                <Select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  label={t('pointsDistribution.selectUser')}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} - {user.department || t('pointsDistribution.noDepartment')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {awardType === 'department' && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>{t('pointsDistribution.selectDepartment')}</InputLabel>
                <Select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  label={t('pointsDistribution.selectDepartment')}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Selected Users Preview */}
            {selectedUsers.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('pointsDistribution.selectedUsers')}:
                </Typography>
                <Box sx={{ maxHeight: 120, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  {selectedUsers.map((user) => (
                    <Chip
                      key={user.id}
                      label={`${user.firstName} ${user.lastName}`}
                      size="small"
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {getSelectedUsersText()}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Points Amount */}
            <TextField
              fullWidth
              label={t('pointsDistribution.points')}
              type="number"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              sx={{ mb: 3 }}
              helperText={t('pointsDistribution.pointsHelperText')}
              inputProps={{ step: 0.01 }}
            />

            {/* Reason - Required */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>{t('pointsDistribution.whyReasonRequired')}</strong> {t('pointsDistribution.reasonRequiredExplanation')}
              </Typography>
              <TextField
                fullWidth
                label={`${t('pointsDistribution.reason')} *`}
                multiline
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('pointsDistribution.reasonPlaceholder')}
                required
                error={!reason.trim()}
                helperText={!reason.trim() ? t('pointsDistribution.reasonRequiredForAll') : t('pointsDistribution.reasonExamples')}
              />
            </Box>

                         {/* Summary */}
             {selectedUsers.length > 0 && points && (
               <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                 <Typography variant="subtitle2" sx={{ mb: 1 }}>
                   {t('pointsDistribution.summary')}:
                 </Typography>
                 <Typography variant="body2">
                   {t('pointsDistribution.awardOrDeduct', {
                     action: parseFloat(points) > 0 ? t('pointsDistribution.award') : t('pointsDistribution.deduct'),
                     points: Math.abs(parseFloat(points)),
                     count: selectedUsers.length,
                     user: selectedUsers.length === 1 ? t('common.user') : t('common.users')
                   })}
                 </Typography>
                 {reason && (
                   <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 'medium' }}>
                     <strong>{t('pointsDistribution.reasonLabel')}</strong> {reason}
                   </Typography>
                 )}
                 {!reason.trim() && (
                   <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                     {t('pointsDistribution.reasonRequiredWarning')}
                   </Typography>
                 )}
               </Box>
             )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('pointsDistribution.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            submitting || 
            selectedUsers.length === 0 || 
            !points || 
            !reason.trim() ||
            (parseFloat(points) > 0 && parseFloat(points) * selectedUsers.length > companyStats.availablePoints)
          }
          startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
        >
          {submitting ? t('pointsDistribution.processing') : t('pointsDistribution.awardPointsButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AwardPoints; 